<template>
  <div class="app-container">
    <!-- 主体区域 -->
    <main class="app-main">
      <div class="cate-container">
        <!-- 工具栏 -->
        <div class="toolbar">
          <div class="left-actions">
            <span class="page-title">分区管理</span>
            <span class="total-count">共 <strong>{{ totalCount }}</strong> 个分区</span>
          </div>
          <div class="right-actions">
            <el-button
              type="primary"
              size="default"
              icon="el-icon-plus"
              @click="openZoneForm()"
            >新建分区</el-button>
            <el-button size="default" icon="el-icon-refresh" @click="loadZones">刷新</el-button>
          </div>
        </div>

        <!-- 表格 -->
        <div class="table-wrap">
          <el-table
            v-loading="loading"
            :data="pagedData"
            stripe
            border
            style="width:100%"
            height="70vh"
            :row-style="{ height: '56px' }"
            :cell-style="{ padding: '8px 12px' }"
          >
            <el-table-column prop="id" label="ID" width="80" align="center" />

            <el-table-column label="分区图片" width="120" align="center">
              <template slot-scope="{ row }">
                <div class="zone-img-cell">
                  <!-- 已有图片：展示缩略图 + 悬停更换 -->
                  <div v-if="row.img" class="img-wrap">
                    <el-image
                      class="zone-thumb"
                      :src="row.img"
                      :preview-src-list="[row.img]"
                      fit="cover"
                    >
                      <div slot="error" class="img-placeholder">
                        <i class="el-icon-picture-outline"></i>
                      </div>
                    </el-image>
                    <div
                      class="img-mask"
                      :class="{ 'is-loading': imgUploadingId === row.id }"
                      @click.stop="triggerImgUpload(row)"
                    >
                      <template v-if="imgUploadingId === row.id">
                        <i class="el-icon-loading"></i>
                        <span>上传中</span>
                      </template>
                      <template v-else>
                        <i class="el-icon-refresh"></i>
                        <span>更换</span>
                      </template>
                    </div>
                  </div>

                  <!-- 无图片：添加图片 -->
                  <div
                    v-else
                    class="add-img-btn"
                    :class="{ 'is-loading': imgUploadingId === row.id }"
                    @click="triggerImgUpload(row)"
                  >
                    <template v-if="imgUploadingId === row.id">
                      <i class="el-icon-loading"></i>
                      <span>上传中</span>
                    </template>
                    <template v-else>
                      <i class="el-icon-plus"></i>
                      <span>添加图片</span>
                    </template>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="分区名称" min-width="200">
              <template slot-scope="{ row }">
                <span class="name-cell">{{ row.name || ('分区#' + row.id) }}</span>
              </template>
            </el-table-column>

            <el-table-column prop="sort" label="排序" width="90" align="center">
              <template slot-scope="{ row }">
                <el-tag size="small" type="warning" effect="plain">{{ row.sort || 0 }}</el-tag>
              </template>
            </el-table-column>

            <el-table-column label="已绑定分类" min-width="280">
              <template slot-scope="{ row }">
                <div v-if="(boundMap[row.id] || []).length" class="bound-tags">
                  <el-tag
                    v-for="item in boundMap[row.id].slice(0, 4)"
                    :key="item.id"
                    size="mini"
                    type="info"
                    effect="plain"
                    class="bound-tag"
                  >{{ item.name }}</el-tag>
                  <span
                    v-if="boundMap[row.id].length > 4"
                    class="bound-more"
                  >+{{ boundMap[row.id].length - 4 }}</span>
                </div>
                <span v-else class="none-text">未绑定</span>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="90" align="center">
              <template slot-scope="{ row }">
                <el-tag
                  size="mini"
                  :type="row.display === 1 ? 'success' : 'info'"
                  effect="plain"
                >{{ row.display === 1 ? '显示' : '隐藏' }}</el-tag>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="230" align="center" fixed="right">
              <template slot-scope="{ row }">
                <div class="action-group">
                  <el-button
                    type="text"
                    size="default"
                    class="action-btn bind-btn"
                    @click="openBindDialog(row)"
                  >
                    <i class="el-icon-connection"></i> 绑定
                  </el-button>
                  <el-button
                    type="text"
                    size="default"
                    class="action-btn edit-btn"
                    @click="openZoneForm(row)"
                  >
                    <i class="el-icon-edit"></i> 编辑
                  </el-button>
                  <el-button
                    type="text"
                    size="default"
                    class="action-btn del-btn"
                    @click="handleDelete(row)"
                  >
                    <i class="el-icon-delete"></i> 删除
                  </el-button>
                </div>
              </template>
            </el-table-column>

            <template slot="empty">
              <div class="empty-state-custom">
                <div class="empty-icon-wrap">
                  <i class="el-icon-s-grid"></i>
                </div>
                <div class="empty-title">暂无分区</div>
              </div>
            </template>
          </el-table>
        </div>

        <!-- 分页 -->
        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalCount"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :current-page.sync="currentPage"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </main>

    <!-- 隐藏的文件选择框：用于列表内上传分区图片 -->
    <input
      ref="listImgInput"
      type="file"
      class="hidden-file-input"
      accept="image/jpeg,image/png,image/gif,image/webp"
      @change="onListImgSelected"
    />

    <!-- 新建/编辑分区弹窗（不含图片） -->
    <el-dialog
      :title="zoneForm.id ? '编辑分区' : '新建分区'"
      :visible.sync="zoneFormVisible"
      width="480px"
      top="12vh"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="dialog-form"
    >
      <el-form
        ref="zoneForm"
        :model="zoneForm"
        :rules="zoneRules"
        label-width="90px"
        size="default"
      >
        <el-form-item label="分区名称" prop="name">
          <el-input
            v-model.trim="zoneForm.name"
            maxlength="30"
            show-word-limit
            placeholder="请输入分区名称"
          />
        </el-form-item>

        <el-form-item label="排序" prop="sort">
          <el-input-number
            v-model="zoneForm.sort"
            :min="0"
            :max="9999"
            controls-position="right"
          />
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>

        <el-form-item label="状态" prop="display">
          <el-radio-group v-model="zoneForm.display">
            <el-radio :label="1">显示</el-radio>
            <el-radio :label="0">隐藏</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="!zoneForm.id" label="提示">
          <span class="form-tip no-margin">
            分区图片可在保存后，在列表中点击「添加图片」上传。
          </span>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="default" @click="zoneFormVisible = false">取 消</el-button>
        <el-button
          type="primary"
          size="default"
          :loading="zoneSaving"
          @click="submitZoneForm"
        >确 定</el-button>
      </span>
    </el-dialog>

    <!-- 绑定分类弹窗 -->
    <el-dialog
      title="绑定分类"
      :visible.sync="bindVisible"
      width="620px"
      top="8vh"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="dialog-form"
    >
      <div class="bind-header">
        <span class="bind-zone">
          <i class="el-icon-collection-tag"></i>
          当前分区：<strong>{{ currentZone.name || ('分区#' + currentZone.id) }}</strong>
        </span>
        <span class="bind-count">
          已选 <strong>{{ selectedIds.length }}</strong> / {{ cateList.length }}
        </span>
      </div>

      <div class="cate-picker" v-loading="cateLoading">
        <el-table
          ref="cateTable"
          :data="cateList"
          height="380"
          border
          stripe
          row-key="id"
          style="width:100%"
          @selection-change="onCateSelectionChange"
        >
          <el-table-column type="selection" width="50" align="center" />

          <el-table-column prop="name" label="分类名称" min-width="260">
            <template slot-scope="{ row }">
              <span class="name-cell">{{ row.name }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="sort" label="排序" width="100" align="center">
            <template slot-scope="{ row }">
              <span class="time-cell">
                {{ (row.sort === null || row.sort === undefined || row.sort === '') ? '—' : row.sort }}
              </span>
            </template>
          </el-table-column>

          <template slot="empty">
            <div class="tree-empty">暂无可绑定的分类</div>
          </template>
        </el-table>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="default" @click="bindVisible = false">取 消</el-button>
        <el-button
          type="primary"
          size="default"
          :loading="binding"
          @click="submitBind"
        >确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import axios from 'axios'
import Cookie from 'js-cookie'

// 接口基础地址
const BASE_URL = '/goods/newgroup_list.php'
const FLBASE_URL = '/rcadmin/homeCate.php'
// 分区图片上传接口
const UPLOAD_URL = 'https://api.gd.91comerp.com/index/upload_fenqu.php'

// ---------- 上传专用 axios 实例（参考轮播图页面，避免 FormData 被 JSON 化） ----------
const token = Cookie.get('token')
const UploadRequest = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    comlistToken: token
  }
})

// 分类类型（item_list / item_add 的 type 参数）
const TYPE_CATE = 1

// 固定店铺ID
const SH_ID = 1

// 图片大小上限（MB）
const MAX_IMG_MB = 10

// 允许的图片类型
const ALLOW_IMG_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

// 新建表单默认值
function defaultZoneForm () {
  return {
    id: null,
    name: '',
    sort: 0,
    display: 1
  }
}

// 兼容后端 img 可能返回 字符串 / 数组 / 对象 的情况
function normalizeImg (val) {
  if (!val) return ''
  if (Array.isArray(val)) return normalizeImg(val[0])
  if (typeof val === 'object') return val.yasuo_url || val.url || ''
  return String(val)
}

// 图片本地校验，返回错误信息（空字符串表示通过）
function validateImageFile (file) {
  if (!file) return '请选择图片'
  if (ALLOW_IMG_TYPES.indexOf(file.type) === -1) {
    return '只支持 jpg / jpeg / png / gif / webp 格式的图片'
  }
  if (file.size / 1024 / 1024 > MAX_IMG_MB) {
    return `图片大小不能超过 ${MAX_IMG_MB}MB`
  }
  return ''
}

export default {
  name: 'ZoneManagement',
  data () {
    return {
      // ---------- 店铺ID（固定为 1） ----------
      shId: SH_ID,

      // ---------- 分区列表 ----------
      allData: [],
      zoneItems: {}, // 分区ID -> 已绑定的分类列表 [{id, ref_id, name, ...}]
      loading: false,
      currentPage: 1,
      pageSize: 20,

      // ---------- 列表内图片上传 ----------
      uploadingZone: null, // 当前正在上传图片的分区行
      imgUploadingId: null, // 上传中的分区ID（用于按钮 loading 态）

      // ---------- 新建/编辑分区 ----------
      zoneFormVisible: false,
      zoneSaving: false,
      zoneForm: defaultZoneForm(),
      zoneRules: {
        name: [
          { required: true, message: '请输入分区名称', trigger: 'blur' },
          { min: 1, max: 30, message: '长度在 1 到 30 个字符', trigger: 'blur' }
        ]
      },

      // ---------- 分类数据（用于绑定） ----------
      cateList: [],
      cateLoading: false,

      // ---------- 绑定弹窗 ----------
      bindVisible: false,
      binding: false,
      currentZone: {},
      selectedIds: [], // 当前勾选的分类ID（ref_id）
      originalBindings: [] // 进入弹窗时后台已有的绑定 [{id: 绑定记录ID, ref_id, name}]
    }
  },

  computed: {
    // 总记录数
    totalCount () {
      return this.allData.length
    },
    // 分页后的数据
    pagedData () {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.allData.slice(start, end)
    },
    // 每个分区已绑定的分类（带名称）
    boundMap () {
      const map = {}
      this.allData.forEach(row => {
        const items = this.zoneItems[row.id] || []
        map[row.id] = items.map(it => ({
          id: Number(it.ref_id),
          name: it.name || `ID:${it.ref_id}`
        }))
      })
      return map
    }
  },

  created () {
    this.loadZones()
  },

  methods: {
    // ==================== 数据加载 ====================

    // ---------- 查询分区列表 ----------
    async loadZones () {
      this.loading = true
      try {
        const res = await request.post(`${BASE_URL}?a=lists`, {
          sh_id: SH_ID
        })

        if (res && String(res.code) === '0') {
          const list = Array.isArray(res.data) ? res.data : []
          this.allData = list.map(row => ({
            id: row.id,
            name: row.bfenlei4 || row.name || '',
            sort: Number(row.bfenlei3 || 0),
            display: Number(row.display) === 1 ? 1 : 0,
            img: normalizeImg(row.img), // 3.7 分区图片
            item_total: Number(row.item_total || 0),
            goods_total: Number(row.goods_total || 0)
          })).sort((a, b) => a.sort - b.sort)

          // 当前页越界时回退
          const maxPage = Math.max(1, Math.ceil(this.allData.length / this.pageSize))
          if (this.currentPage > maxPage) this.currentPage = maxPage

          // 并行拉取每个分区的已绑定分类
          await this.loadZoneDetails()
        } else {
          this.$message.error((res && res.msg) || '加载分区失败')
          this.allData = []
          this.zoneItems = {}
        }
      } catch (err) {
        console.error('加载分区失败:', err)
        this.$message.error('网络错误，请检查连接')
        this.allData = []
        this.zoneItems = {}
      } finally {
        this.loading = false
      }
    },

    // ---------- 批量加载已绑定分类（a=detail） ----------
    async loadZoneDetails () {
      const map = {}
      await Promise.all(this.allData.map(async row => {
        // 没有绑定就跳过，避免无意义请求
        if (!row.item_total) {
          map[row.id] = []
          return
        }
        try {
          const res = await request.post(`${BASE_URL}?a=detail`, { id: row.id })
          if (res && String(res.code) === '0' && res.data) {
            map[row.id] = (res.data.item_list || [])
              .filter(it => Number(it.type) === TYPE_CATE)
          } else {
            map[row.id] = []
          }
        } catch (e) {
          console.error(`加载分区 ${row.id} 详情失败:`, e)
          map[row.id] = []
        }
      }))
      this.zoneItems = map
    },

    // ==================== 分区图片上传（列表内） ====================

    // 触发文件选择
    triggerImgUpload (row) {
      if (!row || !row.id) return
      if (this.imgUploadingId) return // 有上传进行中时忽略

      this.uploadingZone = row
      const input = this.$refs.listImgInput
      if (!input) return
      input.value = '' // 重置，允许重复选择同一文件
      input.click()
    },

    // 选择文件后立即上传
    async onListImgSelected (e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (!file) return

      const row = this.uploadingZone
      if (!row || !row.id) return

      const errMsg = validateImageFile(file)
      if (errMsg) {
        this.$message.error(errMsg)
        e.target.value = ''
        return
      }

      this.imgUploadingId = row.id
      try {
        await this.uploadZoneImage(row.id, file)
        this.$message.success('图片上传成功')
        await this.loadZones()
      } catch (err) {
        console.error('分区图片上传失败:', err)
        this.$message.error((err && err.message) || '图片上传失败，请重试')
      } finally {
        this.imgUploadingId = null
        this.uploadingZone = null
        if (e && e.target) e.target.value = ''
      }
    },

    // ---------- 上传分区图片（multipart/form-data，绕开 JSON 化） ----------
    async uploadZoneImage (zoneId, file) {
      const fd = new FormData()
      fd.append('file', file) // File 对象原样 append
      fd.append('top_id', String(zoneId)) // 绑定到分区
      fd.append('sh_id', String(SH_ID)) // 店铺ID

      const res = await UploadRequest.post(UPLOAD_URL, fd, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const data = res && res.data
      if (!data || String(data.code) !== '0') {
        throw new Error((data && data.msg) || '图片上传失败')
      }
      return data
    },

    // ==================== 新建/编辑分区 ====================

    // 打开表单：无参数=新建，有参数=编辑
    openZoneForm (row) {
      this.zoneForm = row
        ? {
          id: row.id,
          name: row.name || '',
          sort: Number(row.sort || 0),
          display: Number(row.display) === 1 ? 1 : 0
        }
        : defaultZoneForm()

      this.zoneFormVisible = true
      this.$nextTick(() => {
        this.$refs.zoneForm && this.$refs.zoneForm.clearValidate()
      })
    },

    // 提交表单
    async submitZoneForm () {
      const form = this.$refs.zoneForm
      if (!form) return

      try {
        await form.validate()
      } catch (e) {
        return
      }

      this.zoneSaving = true
      try {
        const isEdit = !!this.zoneForm.id
        const url = isEdit
          ? `${BASE_URL}?a=edit`
          : `${BASE_URL}?a=add`

        const params = isEdit
          ? {
            id: this.zoneForm.id,
            name: this.zoneForm.name,
            sort: Number(this.zoneForm.sort || 0),
            display: Number(this.zoneForm.display) === 1 ? 1 : 0
          }
          : {
            sh_id: SH_ID,
            name: this.zoneForm.name,
            sort: Number(this.zoneForm.sort || 0),
            display: Number(this.zoneForm.display) === 1 ? 1 : 0
          }

        const res = await request.post(url, params)
        if (res && String(res.code) === '0') {
          this.$message.success(isEdit ? '编辑成功' : '添加成功')
          this.zoneFormVisible = false
          this.loadZones()
        } else {
          this.$message.error((res && res.msg) || (isEdit ? '编辑失败' : '添加失败'))
        }
      } catch (err) {
        console.error('保存分区失败:', err)
        this.$message.error('网络错误，请重试')
      } finally {
        this.zoneSaving = false
      }
    },

    // 删除分区
    async handleDelete (row) {
      try {
        await this.$confirm(
          `确定要删除分区「${row.name || ('#' + row.id)}」吗？删除后将同时解除其绑定的分类和直挂商品。`,
          '删除确认',
          {
            type: 'warning',
            confirmButtonText: '确定删除',
            cancelButtonText: '取消'
          }
        )
      } catch (e) {
        return // 用户取消
      }

      try {
        const res = await request.post(`${BASE_URL}?a=delete`, { id: row.id })
        if (res && String(res.code) === '0') {
          this.$message.success('删除成功')
          this.loadZones()
        } else {
          this.$message.error((res && res.msg) || '删除失败')
        }
      } catch (err) {
        console.error('删除分区失败:', err)
        this.$message.error('网络错误，请重试')
      }
    },

    // ==================== 绑定分类 ====================

    // 打开绑定弹窗
    async openBindDialog (row) {
      this.currentZone = row
      this.cateList = []
      this.selectedIds = []
      this.originalBindings = []
      this.bindVisible = true
      this.cateLoading = true

      try {
        // 并行：可绑定分类列表 + 分区详情（已绑定项）
        const [itemRes, detailRes] = await Promise.all([
          request.post(`${FLBASE_URL}?a=select`, {
            page: 1,
            page_size: 1000

          }),
          request.post(`${BASE_URL}?a=detail`, { id: row.id })
        ])

        // 可绑定分类
        if (itemRes && String(itemRes.code) === '0') {
          const arr = Array.isArray(itemRes.res) ? itemRes.res : []
          this.cateList = arr.map(it => ({
            id: Number(it.id),
            name: it.name || `ID:${it.id}`,
            sort: it.sort
          })).sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
        }

        // 已绑定项（含绑定记录 id，用于解绑）
        if (detailRes && String(detailRes.code) === '0' && detailRes.data) {
          const items = (detailRes.data.item_list || [])
            .filter(it => Number(it.type) === TYPE_CATE)
          this.originalBindings = items.map(it => ({
            id: Number(it.id), // 绑定记录ID（sh_newgroup_item.id）
            ref_id: Number(it.ref_id), // 分类ID
            name: it.name || ''
          }))
          this.selectedIds = this.originalBindings.map(b => b.ref_id)
        }

        this.$nextTick(() => this.applySelection())
      } catch (err) {
        console.error('加载绑定数据失败:', err)
        this.$message.error('加载数据失败')
      } finally {
        this.cateLoading = false
      }
    },

    // 回显已勾选分类
    applySelection () {
      const table = this.$refs.cateTable
      if (!table) return

      const target = this.selectedIds.slice()
      table.clearSelection()
      this.cateList.forEach(row => {
        if (target.indexOf(Number(row.id)) > -1) {
          table.toggleRowSelection(row, true)
        }
      })
    },

    // 表格勾选变化
    onCateSelectionChange (rows) {
      this.selectedIds = rows.map(row => Number(row.id))
    },

    // 提交绑定：差量对比 → item_add / item_del
    async submitBind () {
      if (!this.currentZone || !this.currentZone.id) return

      this.binding = true
      try {
        const originalRefIds = this.originalBindings.map(b => b.ref_id)
        const currentIds = this.selectedIds.slice()

        // 需要新增的
        const toAdd = currentIds.filter(id => originalRefIds.indexOf(id) === -1)
        // 需要解绑的
        const toDel = this.originalBindings.filter(b => currentIds.indexOf(b.ref_id) === -1)

        if (!toAdd.length && !toDel.length) {
          this.$message.info('未做任何修改')
          this.bindVisible = false
          return
        }

        const tasks = []

        if (toAdd.length) {
          tasks.push(request.post(`${BASE_URL}?a=item_add`, {
            newgroup_id: this.currentZone.id,
            type: TYPE_CATE,
            ref_ids: toAdd
          }))
        }

        toDel.forEach(b => {
          tasks.push(request.post(`${BASE_URL}?a=item_del`, { id: b.id }))
        })

        const results = await Promise.all(tasks)
        const failed = results.filter(r => !r || String(r.code) !== '0')

        if (failed.length) {
          this.$message.warning(`部分操作失败（${failed.length}/${results.length}），请刷新后重试`)
        } else {
          this.$message.success('绑定成功')
        }

        this.bindVisible = false
        this.loadZones()
      } catch (err) {
        console.error('绑定失败:', err)
        this.$message.error('网络错误，请重试')
      } finally {
        this.binding = false
      }
    },

    // ==================== 分页事件 ====================
    handleSizeChange (val) {
      this.pageSize = val
      this.currentPage = 1
    },
    handleCurrentChange (val) {
      this.currentPage = val
    }
  }
}
</script>

<style lang="scss" scoped>
/* ===== 全局布局 ===== */
.app-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  font-family: 'Helvetica Neue', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ===== 主体区域 ===== */
.app-main {
  flex: 1;
  padding: 20px 24px 24px 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ===== 分区容器 ===== */
.cate-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 20px 24px 24px 24px;
}

/* ===== 工具栏 ===== */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 12px;

  .left-actions {
    display: flex;
    align-items: center;
    gap: 16px;

    .page-title {
      font-size: 17px;
      font-weight: 600;
      color: #1d2129;
      position: relative;
      padding-left: 12px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 16px;
        border-radius: 2px;
        background: linear-gradient(180deg, #1890ff, #40a9ff);
      }
    }
  }

  .right-actions {
    display: flex;
    align-items: center;
    gap: 10px;

    .el-button {
      color: #4e5969;
      &:hover {
        color: #1890ff;
      }
      &.el-button--primary {
        color: #fff;
      }
    }
  }

  .total-count {
    font-size: 14px;
    color: #86909c;
    padding: 4px 14px;
    background: #f5f6f8;
    border-radius: 20px;

    strong {
      color: #1d2129;
      font-weight: 600;
      font-size: 16px;
    }
  }
}

/* ===== 表格容器 ===== */
.table-wrap {
  flex: 1;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
  border: 1px solid #eaedf2;

  .el-table {
    height: 100%;
    border-radius: 8px;

    .el-table__header-wrapper {
      .el-table__header {
        th {
          background-color: #f7f9fc !important;
          font-weight: 600;
          color: #1d2129;
          font-size: 14px;
          padding: 12px 12px;
          border-bottom: 2px solid #e8ecf1;
        }
      }
    }

    .el-table__body-wrapper {
      overflow-y: auto;

      .el-table__row {
        transition: background 0.2s;

        &:hover {
          background: #f5f9ff !important;
        }
      }
    }

    .el-table__empty-block {
      min-height: 280px;
    }
  }
}

/* ===== 单元格样式 ===== */
.name-cell {
  font-weight: 500;
  color: #1d2129;
  font-size: 14px;
  display: inline-block;
}

.time-cell {
  color: #86909c;
  font-size: 13px;
}

/* ===== 分区图片（列表） ===== */
.zone-img-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 已有图片 */
.img-wrap {
  position: relative;
  width: 72px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #eaedf2;
  background: #f7f9fc;

  .zone-thumb {
    width: 100%;
    height: 100%;
    display: block;
    cursor: pointer;
  }

  .img-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 11px;
    line-height: 1.1;
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.2s;

    i {
      font-size: 15px;
    }

    &.is-loading {
      opacity: 1;
      cursor: default;
    }
  }

  &:hover .img-mask {
    opacity: 1;
  }
}

/* 无图片：添加按钮 */
.add-img-btn {
  width: 72px;
  height: 48px;
  border-radius: 6px;
  border: 1px dashed #c9d3e0;
  background: #fafbfc;
  color: #7a8699;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 11px;
  line-height: 1.1;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;

  i {
    font-size: 15px;
    color: #a3b0c2;
    transition: color 0.2s;
  }

  &:hover {
    border-color: #1890ff;
    color: #1890ff;
    background: #f2f8ff;

    i {
      color: #1890ff;
    }
  }

  &.is-loading {
    cursor: default;
    border-color: #d6e4ff;
    color: #1890ff;
    background: #f5f9ff;

    i {
      color: #1890ff;
    }
  }
}

.img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c4ccd8;
  font-size: 16px;
}

/* 隐藏的文件选择框 */
.hidden-file-input {
  display: none;
}

/* 已绑定分类标签 */
.bound-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;

  .bound-tag {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: #f2f6ff;
    border-color: #d6e4ff;
    color: #4a6fa5;
  }

  .bound-more {
    font-size: 12px;
    color: #86909c;
    background: #f5f6f8;
    border-radius: 10px;
    padding: 1px 8px;
  }
}

.none-text {
  color: #b0b8c4;
  font-size: 13px;
}

/* ===== 操作按钮组 ===== */
.action-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;

  .action-btn {
    padding: 0 6px;
    font-size: 13px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 3px;

    i {
      font-size: 14px;
    }

    & + .action-btn {
      margin-left: 0;
    }
  }

  .bind-btn {
    color: #1890ff;
    &:hover {
      color: #40a9ff;
    }
  }

  .edit-btn {
    color: #52c41a;
    &:hover {
      color: #73d13d;
    }
  }

  .del-btn {
    color: #ff4d4f;
    &:hover {
      color: #ff7875;
    }
  }
}

/* ===== 空状态 ===== */
.empty-state-custom {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;

  .empty-icon-wrap {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    transition: all 0.3s;

    .el-icon-s-grid {
      font-size: 40px;
      color: #b7c0cc;
      transition: all 0.3s;
    }
  }

  &:hover .empty-icon-wrap {
    background: #e8f0fe;
    .el-icon-s-grid {
      color: #1890ff;
    }
  }

  .empty-title {
    font-size: 17px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 4px;
  }
}

/* ===== 分页 ===== */
.pagination-wrap {
  flex-shrink: 0;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f5;
  margin-top: 16px;

  .el-pagination {
    .el-pagination__total {
      font-weight: 400;
      color: #4e5969;
    }
    .el-pagination__sizes {
      .el-select .el-input__inner {
        border-radius: 6px;
      }
    }
    .el-pager li {
      border-radius: 6px;
      margin: 0 2px;
      &.active {
        background: #1890ff;
        color: #fff;
      }
    }
    .btn-prev,
    .btn-next {
      border-radius: 6px;
    }
  }
}

/* ===== 弹窗表单 ===== */
.dialog-form {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
      padding: 20px 24px 16px 24px;
      border-bottom: 1px solid #f0f2f5;
      background: #fafbfc;

      .el-dialog__title {
        font-size: 17px;
        font-weight: 600;
        color: #1d2129;
      }
    }

    .el-dialog__body {
      padding: 20px 24px 8px 24px;
    }

    .el-dialog__footer {
      padding: 12px 24px 20px 24px;
      border-top: 1px solid #f0f2f5;
      background: #fafbfc;
    }
  }

  .form-tip {
    margin-left: 10px;
    color: #b0b8c4;
    font-size: 12px;

    &.no-margin {
      margin-left: 0;
      line-height: 1.6;
    }
  }
}

/* ===== 绑定弹窗内容 ===== */
.bind-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: #f7f9fc;
  border: 1px solid #eaedf2;
  border-radius: 8px;
  font-size: 13px;
  color: #4e5969;

  .bind-zone {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    i {
      color: #1890ff;
      font-size: 15px;
    }

    strong {
      color: #1d2129;
      font-weight: 600;
    }
  }

  .bind-count {
    strong {
      color: #1890ff;
      font-weight: 600;
      font-size: 15px;
      margin: 0 2px;
    }
  }
}

.cate-picker {
  border-radius: 8px;
  overflow: hidden;

  .el-table {
    border-radius: 8px;

    .el-table__header {
      th {
        background-color: #f7f9fc !important;
        font-weight: 600;
        color: #1d2129;
        font-size: 13px;
      }
    }

    .el-table__row:hover {
      background: #f5f9ff !important;
    }
  }

  .tree-empty {
    padding: 40px 0;
    color: #b0b8c4;
    font-size: 14px;
    text-align: center;
  }
}

/* ===== 响应式 ===== */
@media screen and (max-width: 768px) {
  .app-main {
    padding: 12px 12px 16px 12px;
  }
  .cate-container {
    padding: 14px 14px 16px 14px;
    border-radius: 8px;
  }
  .toolbar {
    flex-direction: column;
    align-items: stretch;
    .left-actions,
    .right-actions {
      justify-content: space-between;
      flex-wrap: wrap;
    }
  }
  .dialog-form .el-dialog {
    width: 94% !important;
    margin: 0 auto;
  }
  .pagination-wrap {
    .el-pagination {
      .el-pagination__sizes {
        display: none;
      }
      .el-pagination__jump {
        display: none;
      }
    }
  }
}
</style>
